import React, { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { euro } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TIPI = { noleggio: "Noleggio", carburante: "Carburante", pedaggio: "Pedaggio" };

function Campo({ label, k, step = "0.01", value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type="number" min="0" step={step} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`modifica-vei-${k}`} />
    </div>
  );
}

export function ModificaVeicoloCosto({ riga, veicoli, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (riga)
      setF({
        veicolo_id: riga.veicolo_id || "",
        tipo: riga.tipo,
        data: (riga.data || "").slice(0, 10),
        giorni: String(riga.giorni ?? ""),
        costo_giornaliero: String(riga.costo_giornaliero ?? ""),
        km: String(riga.km ?? ""),
        litri: String(riga.litri ?? ""),
        costo_litro: String(riga.costo_litro ?? ""),
        importo: String(riga.importo ?? ""),
        descrizione: riga.descrizione || "",
      });
  }, [riga]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const mut = useMutation({
    mutationFn: async (p) => (await api.put(`/veicoli-costi/${riga.id}`, p)).data,
    onSuccess: () => { toast.success("Costo veicolo aggiornato"); onSaved(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const salva = () => {
    if (!f.veicolo_id || !f.data) return toast.error("Compila veicolo e data");
    const p = { veicolo_id: f.veicolo_id, tipo: f.tipo, data: f.data, descrizione: f.descrizione };
    if (f.tipo === "noleggio") { p.giorni = Number(f.giorni) || 0; p.costo_giornaliero = Number(f.costo_giornaliero) || 0; }
    else if (f.tipo === "carburante") { p.km = Number(f.km) || 0; p.litri = Number(f.litri) || 0; p.costo_litro = Number(f.costo_litro) || 0; }
    else p.importo = Number(f.importo) || 0;
    mut.mutate(p);
  };
  const veicoloSel = veicoli.find((v) => v.id === f?.veicolo_id);
  const consumo = Number(veicoloSel?.consumo_l_100km) || 0;
  const setKm = (v) => setF((s) => ({ ...s, km: v, litri: consumo > 0 && v !== "" ? String(Math.round((Number(v) || 0) * consumo) / 100) : s.litri }));
  const C = (label, k, step) => <Campo label={label} k={k} step={step} value={f[k]} onChange={k === "km" ? setKm : set(k)} />;
  return (
    <Dialog open={!!riga} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="modifica-vei-dialog">
        <DialogHeader><DialogTitle className="font-display">Modifica costo veicolo · {TIPI[riga?.tipo]}</DialogTitle></DialogHeader>
        {f && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label>Veicolo</Label>
              <Select value={f.veicolo_id} onValueChange={set("veicolo_id")}>
                <SelectTrigger data-testid="modifica-vei-veicolo"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{veicoli.map((v) => <SelectItem key={v.id} value={v.id}>{v.targa} · {v.modello} — {euro(v.costo_giornaliero)}/g</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Input type="date" value={f.data} onChange={(e) => set("data")(e.target.value)} data-testid="modifica-vei-data" />
            </div>
            {f.tipo === "noleggio" && <>{C("Giorni", "giorni", "0.5")}{C("Importo giornaliero (€)", "costo_giornaliero")}</>}
            {f.tipo === "carburante" && <>{C("Km percorsi", "km", "1")}{C("Litri", "litri")}{C("€/litro", "costo_litro", "0.001")}</>}
            {f.tipo === "pedaggio" && C("Importo (€)", "importo")}
            <div className="space-y-1.5 col-span-2">
              <Label>Descrizione</Label>
              <Input value={f.descrizione} onChange={(e) => set("descrizione")(e.target.value)} data-testid="modifica-vei-descrizione" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="modifica-vei-annulla">Annulla</Button>
          <Button onClick={salva} disabled={mut.isPending} data-testid="modifica-vei-salva">{mut.isPending ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
